/**
 * Contagem regressiva da janela do Vale Pedágio na grid de Expedição —
 * TMS-8134 (ação A6, defeito D2).
 *
 * **Mostrar o prazo antes de a janela fechar é o ponto inteiro da A6.** O caso medido
 * na carga 9147895: às 17:50:37 o sistema recusa com *"Só é possível cancelar o Vale
 * Pedágio com o cálculo estornado"*; o operador estorna; às 17:54:15 recebe *"Só é
 * possível cancelar Vale Pedágio comprado a menos de 3 horas"*. O VP fora comprado às
 * 12:12 — a janela já havia fechado antes da primeira tentativa. **Satisfazer uma
 * pré-condição consome o tempo que expira a outra**, e o operador não tinha como ver o
 * relógio correndo. A carga ficou 69 horas presa.
 *
 * Duas regras que este módulo existe para garantir:
 *
 * 1. **O limite nunca é 3 horas no front.** Ele vem de
 *    `b_config_blogistic.qtde_horas_pedagio`, por cliente, e chega em
 *    `valePedagio.limite_horas` (contrato C-02). O diagnóstico original errou
 *    exatamente aí. Até o limiar de urgência é fração da janela real, não um número
 *    de horas cravado.
 * 2. **Sem `limite_horas` não há contagem.** `limite_horas: null` significa cliente sem
 *    configuração, e nesse estado a Target recusa *sempre*. A tela diz "prazo
 *    indeterminado" — jamais "ainda dá tempo".
 *
 * Contrato de entrada — `ExpedicaoResource.valePedagio`, ao lado do `cargaValePedagio`
 * que continua intacto:
 * `{tipo, origem, limite_horas, comprado_em, expira_em, expirado, horas_restantes}`.
 * `null` quando não há VP; **ausente** quando a relação não foi carregada.
 *
 * Módulo puro (sem DOM, sem Vue): ver `jest.config.js`.
 */

import { formatarDataHora, formatarHorasRestantes } from "./sequenciaCancelamento";

export const UrgenciaPrazo = Object.freeze({
  EXPIRADO: "expirado",
  CRITICO: "critico",
  ATENCAO: "atencao",
  INDETERMINADO: "indeterminado",
});

/**
 * Fração da janela a partir da qual a contagem vira crítica. É fração de propósito:
 * com janela de 3h o alerta começa com 1h restante; com 24h, com 8h. Cravar "1 hora"
 * aqui seria a mesma classe de erro que cravar "3 horas".
 */
const FRACAO_CRITICA = 1 / 3;

const CLASSE = Object.freeze({
  [UrgenciaPrazo.EXPIRADO]: "badge badge-danger",
  [UrgenciaPrazo.CRITICO]: "badge badge-danger",
  [UrgenciaPrazo.ATENCAO]: "badge badge-warning",
  [UrgenciaPrazo.INDETERMINADO]: "badge badge-secondary",
});

const ORIGEM_TARGET = "regra do fornecedor Target";

const OCULTO = Object.freeze({
  visivel: false,
  urgencia: null,
  texto: "",
  titulo: "",
  classe: "",
});

function numero(valor) {
  if (valor == null || valor === "") {
    return null;
  }
  const convertido = Number(valor);
  return Number.isFinite(convertido) ? convertido : null;
}

function janelaLegivel(limiteHoras) {
  return limiteHoras == null ? "janela do fornecedor" : `${limiteHoras}h do fornecedor`;
}

function tituloDaJanela(prazo, limiteHoras) {
  const compradoEm = formatarDataHora(prazo.comprado_em || prazo.compradoEm);
  const expiraEm = formatarDataHora(prazo.expira_em || prazo.expiraEm);
  const partes = [];

  if (compradoEm) {
    partes.push(`Vale Pedágio comprado em ${compradoEm}`);
  }

  if (limiteHoras == null) {
    partes.push(
      "O limite de cancelamento não está configurado para este cliente, então o " +
        "iSend não consegue calcular o prazo"
    );
  } else if (prazo.expirado) {
    partes.push(
      `A janela de ${janelaLegivel(limiteHoras)} ${
        expiraEm ? `expirou em ${expiraEm}` : "já expirou"
      }`
    );
  } else {
    partes.push(
      `A janela de ${janelaLegivel(limiteHoras)} ${
        expiraEm ? `expira em ${expiraEm}` : "ainda está aberta"
      }`
    );
  }

  partes.push(`Cancelar depois disso é ${ORIGEM_TARGET}, não uma falha do iSend`);

  return `${partes.join(". ")}.`;
}

function resultado(urgencia, texto, titulo) {
  return {
    visivel: true,
    urgencia,
    texto,
    titulo,
    classe: CLASSE[urgencia],
  };
}

/**
 * Estado da janela do Vale Pedágio para uma linha da grid.
 *
 * @param {object|null|undefined} rowData linha do `ExpedicaoResource`
 * @returns {{visivel: boolean, urgencia: string|null, texto: string,
 *            titulo: string, classe: string}}
 */
export function prazoValePedagioDaLinha(rowData) {
  const linha = rowData && typeof rowData === "object" ? rowData : {};
  const prazo =
    linha.valePedagio && typeof linha.valePedagio === "object"
      ? linha.valePedagio
      : null;

  if (!prazo) {
    return { ...OCULTO };
  }

  const limiteHoras = numero(prazo.limite_horas != null ? prazo.limite_horas : prazo.limiteHoras);
  const titulo = tituloDaJanela(prazo, limiteHoras);

  /* Sem limite conhecido não existe deadline a comunicar — e o backend omite a chave
     `prazo` no bloqueio pelo mesmo motivo. */
  if (limiteHoras == null || limiteHoras <= 0) {
    return resultado(
      UrgenciaPrazo.INDETERMINADO,
      "VP: prazo indeterminado",
      titulo
    );
  }

  if (prazo.expirado) {
    return resultado(UrgenciaPrazo.EXPIRADO, "VP: janela encerrada", titulo);
  }

  const restantes = numero(
    prazo.horas_restantes != null ? prazo.horas_restantes : prazo.horasRestantes
  );
  const rotulo = formatarHorasRestantes(restantes);

  if (rotulo === "") {
    /* Ainda não expirou, mas o que resta não chega a um minuto. Dizer "faltam 0min"
       leria como se houvesse tempo. */
    return resultado(UrgenciaPrazo.CRITICO, "VP: janela fechando", titulo);
  }

  const critico = restantes / limiteHoras <= FRACAO_CRITICA;

  return resultado(
    critico ? UrgenciaPrazo.CRITICO : UrgenciaPrazo.ATENCAO,
    `VP: faltam ${rotulo}`,
    titulo
  );
}
